import { useState } from 'react';
import { supabase } from '../supabaseClient';

/**
 * Hook para chamar a Edge Function 'detect-ofertas'.
 * Retorna sugestões de ofertas não cadastradas baseadas em análise de emails.
 */
export const useDetectOfertas = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const detectar = async (areaId) => {
    if (!areaId) {
      setError('ID da área é obrigatório');
      return null;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const { data, error: fnErr } = await supabase.functions.invoke('detect-ofertas', {
        body: { area_id: areaId }
      });

      if (fnErr) throw new Error(fnErr.message || 'Erro ao chamar função');
      if (!data) throw new Error('Resposta vazia');
      if (data.error) throw new Error(data.error);

      setResult(data);
      return data;
    } catch (err) {
      console.error('Erro em detect-ofertas:', err);
      setError(err.message || 'Erro desconhecido');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const limpar = () => {
    setResult(null);
    setError(null);
  };

  /**
   * Constrói o texto formatado da nota de detecção que ficará no timeline.
   * Inclui todas as informações ricas geradas pela IA na detecção.
   */
  const buildNotaDeteccao = (sugestao, areaNome) => {
    const linhas = [];

    linhas.push(`📌 Oferta criada automaticamente pela detecção AI desta área.`);
    linhas.push('');

    if (sugestao.confianca) {
      const confLabel = {
        alta: 'Alta ✅',
        media: 'Média ⚠️',
        baixa: 'Baixa ⚠️',
      }[sugestao.confianca.toLowerCase()] || sugestao.confianca;
      linhas.push(`Confiança: ${confLabel}`);
    }

    if (sugestao.estagio_sugerido) {
      linhas.push(`Estágio sugerido: ${sugestao.estagio_sugerido}`);
    }

    if (sugestao.primeira_abordagem || sugestao.ultima_interacao) {
      linhas.push('');
      if (sugestao.primeira_abordagem) {
        linhas.push(`Primeira abordagem identificada: ${sugestao.primeira_abordagem}`);
      }
      if (sugestao.ultima_interacao) {
        linhas.push(`Última interação identificada: ${sugestao.ultima_interacao}`);
      }
    }

    if (sugestao.contatos_envolvidos && sugestao.contatos_envolvidos.length > 0) {
      linhas.push('');
      linhas.push(`Contatos envolvidos:`);
      sugestao.contatos_envolvidos.forEach(c => linhas.push(`  • ${c}`));
    }

    if (sugestao.evidencias && sugestao.evidencias.length > 0) {
      linhas.push('');
      linhas.push(`Evidências encontradas nos emails:`);
      sugestao.evidencias.forEach(e => linhas.push(`  • "${e}"`));
    }

    if (sugestao.observacoes) {
      linhas.push('');
      linhas.push(`Observação da IA:`);
      linhas.push(sugestao.observacoes);
    }

    linhas.push('');
    linhas.push(`---`);
    linhas.push(`Análise baseada em ${sugestao.emails_analisados || '?'} emails da empresa, ${sugestao.palavras_distintas_match || '?'} palavra(s)-chave matched.`);

    return linhas.join('\n');
  };

  /**
   * Cria ofertas em lote + salva nota IA no deal_context para cada uma.
   * Best-effort: se a nota falhar, a oferta ainda é mantida (não desfaz).
   */
  const criarOfertasEmLote = async (areaId, areaNome, sugestoesAprovadas, responsavelId) => {
    if (!areaId || !sugestoesAprovadas || sugestoesAprovadas.length === 0) {
      throw new Error('Nenhuma sugestão selecionada');
    }

    // Monta payloads das ofertas
    const ofertasPayload = sugestoesAprovadas.map(s => ({
      area_id: areaId,
      empresa_id: s.empresa_id,
      nome: `${s.empresa_nome}: ${areaNome}`,
      descricao: s.observacoes || null,
      status: s.estagio_sugerido || '2.0.Lead_Qualificado',
      responsavel_id: responsavelId || null,
      data_envio_proposta: s.primeira_abordagem || null,
      data_ultimo_contato: s.ultima_interacao || null,
    }));

    // 1. Insere ofertas em lote
    const { data: ofertasCriadas, error: ofertaErr } = await supabase
      .from('ofertas')
      .insert(ofertasPayload)
      .select();

    if (ofertaErr) throw ofertaErr;
    if (!ofertasCriadas || ofertasCriadas.length === 0) {
      throw new Error('Nenhuma oferta foi criada');
    }

    // 2. Para cada oferta criada, monta a nota correspondente em deal_context
    // Mapeia oferta criada de volta à sua sugestão original (por empresa_id)
    const notasPayload = ofertasCriadas.map(oferta => {
      const sugestao = sugestoesAprovadas.find(s => s.empresa_id === oferta.empresa_id);
      if (!sugestao) return null;

      return {
        oferta_id: oferta.id,
        autor_tipo: 'ia',
        autor_id: 'agent_detector_ofertas',
        tipo: 'nota',
        canal_origem: 'ai_detection',
        texto: buildNotaDeteccao(sugestao, areaNome),
        metadata: {
          fonte: 'detect-ofertas',
          confianca: sugestao.confianca,
          estagio_sugerido_original: sugestao.estagio_sugerido,
          palavras_distintas_match: sugestao.palavras_distintas_match,
          emails_analisados: sugestao.emails_analisados,
          score_relevancia: sugestao.score_relevancia,
          area_nome: areaNome,
          contatos_envolvidos: sugestao.contatos_envolvidos || [],
          evidencias: sugestao.evidencias || [],
          observacoes_originais: sugestao.observacoes || null,
          detectado_em: new Date().toISOString(),
        },
      };
    }).filter(Boolean);

    // 3. Insere notas em lote (best-effort: se falhar, ofertas ficam)
    let notasCriadas = 0;
    if (notasPayload.length > 0) {
      const { data: notas, error: notaErr } = await supabase
        .from('deal_context')
        .insert(notasPayload)
        .select();

      if (notaErr) {
        console.error('Erro ao salvar notas de detecção (ofertas mantidas):', notaErr);
        // Não lança erro — ofertas foram criadas com sucesso
      } else {
        notasCriadas = (notas || []).length;
      }
    }

    return {
      success: true,
      ofertas: ofertasCriadas,
      count: ofertasCriadas.length,
      notas_criadas: notasCriadas,
    };
  };

  return { loading, error, result, detectar, limpar, criarOfertasEmLote };
};
